import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Calendar, User, ArrowRight, BookOpen, Settings } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import axios from 'axios';
import { SEO } from '@/components/seo/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BlogPage = () => {
  const { t, language } = useLanguage();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/blog`);
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching blog posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const getLocalizedContent = (post, field) => {
    const localizedField = `${field}_${language}`;
    return post[localizedField] || post[`${field}_nl`] || '';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'nl' ? 'nl-NL' : language === 'de' ? 'de-DE' : language === 'fr' ? 'fr-FR' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const blogTranslations = {
    nl: {
      title: 'Blog',
      subtitle: 'Tips, nieuws en inzichten over webdesign en online marketing',
      readMore: 'Lees meer',
      noPosts: 'Nog geen artikelen',
      noPostsDesc: 'Er zijn nog geen blog artikelen gepubliceerd.',
      manage: 'Blog beheren',
      seoTitle: 'Blog - Tips & Nieuws over Webdesign',
      seoDesc: 'Lees onze blog voor tips en inzichten over webdesign, SEO en online marketing.'
    },
    en: {
      title: 'Blog',
      subtitle: 'Tips, news and insights about web design and online marketing',
      readMore: 'Read more',
      noPosts: 'No articles yet',
      noPostsDesc: 'No blog articles have been published yet.',
      manage: 'Manage blog',
      seoTitle: 'Blog - Web Design Tips & News',
      seoDesc: 'Read our blog for tips and insights about web design, SEO and online marketing.'
    },
    fr: {
      title: 'Blog',
      subtitle: 'Conseils, actualités et informations sur le web design et le marketing en ligne',
      readMore: 'Lire la suite',
      noPosts: 'Pas encore d\'articles',
      noPostsDesc: 'Aucun article de blog n\'a encore été publié.',
      manage: 'Gérer le blog',
      seoTitle: 'Blog - Conseils & Actualités Web Design',
      seoDesc: 'Lisez notre blog pour des conseils sur le web design, SEO et marketing en ligne.'
    },
    de: {
      title: 'Blog',
      subtitle: 'Tipps, Neuigkeiten und Einblicke in Webdesign und Online-Marketing',
      readMore: 'Weiterlesen',
      noPosts: 'Noch keine Artikel',
      noPostsDesc: 'Es wurden noch keine Blog-Artikel veröffentlicht.',
      manage: 'Blog verwalten',
      seoTitle: 'Blog - Webdesign Tipps & News',
      seoDesc: 'Lesen Sie unseren Blog für Tipps über Webdesign, SEO und Online-Marketing.'
    }
  };

  const txt = blogTranslations[language] || blogTranslations.nl;

  if (loading) {
    return (
      <main className="min-h-screen bg-white pt-32">
        <div className="max-w-7xl mx-auto px-6 flex justify-center py-20">
          <div className="w-8 h-8 border-4 border-[#2563EB] border-t-transparent rounded-full animate-spin" />
        </div>
      </main>
    );
  }

  return (
    <main data-testid="blog-page">
      <SEO 
        title={txt.seoTitle}
        description={txt.seoDesc}
        keywords="webdesign blog, website tips, SEO tips, online marketing blog"
      />
      {/* Hero */}
      <section className="pt-32 pb-20 bg-gradient-to-b from-[#f8f9fc] to-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <div className="inline-flex items-center gap-2 bg-[#2563EB]/10 text-[#2563EB] px-4 py-2 rounded-full text-sm font-medium mb-6">
              <BookOpen size={16} />
              <span>Netblitz {txt.title}</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#0f172a] mb-6">
              <span className="text-gradient">{txt.title}</span>
            </h1>
            <p className="text-xl text-[#475569]">
              {txt.subtitle}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto">
          {posts.length === 0 ? (
            <div className="text-center py-12">
              <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-[#0f172a] mb-2">{txt.noPosts}</h3>
              <p className="text-[#475569] mb-6">{txt.noPostsDesc}</p>
              <Link
                to="/blog-admin"
                className="inline-flex items-center gap-2 text-[#2563EB] font-medium hover:underline"
              >
                <Settings size={16} />
                {txt.manage}
              </Link>
            </div>
          ) : (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {posts.map((post, index) => (
                  <motion.article
                    key={post.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-xl hover:border-[#2563EB]/20 transition-all group"
                    data-testid={`blog-post-${index}`}
                  >
                    {/* Image */}
                    <div className="aspect-[16/10] overflow-hidden bg-slate-100">
                      {post.image_url ? (
                        <img
                          src={post.image_url}
                          alt={getLocalizedContent(post, 'title')}
                          loading="lazy"
                          decoding="async"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#2563EB]/10 to-[#2563EB]/5">
                          <BookOpen className="w-12 h-12 text-[#2563EB]/30" />
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="p-6">
                      {post.category && (
                        <span className="text-xs font-medium text-[#2563EB] bg-[#2563EB]/10 px-3 py-1 rounded-full">
                          {post.category}
                        </span>
                      )}
                      
                      <h2 className="text-xl font-bold text-[#0f172a] mt-3 mb-2 line-clamp-2 group-hover:text-[#2563EB] transition-colors">
                        {getLocalizedContent(post, 'title')}
                      </h2>
                      
                      <p className="text-[#475569] text-sm line-clamp-3 mb-4">
                        {getLocalizedContent(post, 'excerpt')}
                      </p>

                      <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                        <div className="flex items-center gap-4 text-sm text-[#94a3b8]">
                          <span className="flex items-center gap-1">
                            <User size={14} />
                            {post.author}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar size={14} />
                            {formatDate(post.published_at)}
                          </span>
                        </div>
                      </div>

                      <Link
                        to={`/blog/${post.slug}`}
                        className="inline-flex items-center gap-2 text-[#2563EB] font-medium mt-4 hover:gap-3 transition-all"
                      >
                        {txt.readMore}
                        <ArrowRight size={16} />
                      </Link>
                    </div>
                  </motion.article>
                ))}
              </div>

              {/* Admin link */}
              <div className="mt-12 text-center">
                <Link
                  to="/blog-admin"
                  className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#2563EB] transition-colors"
                >
                  <Settings size={14} />
                  {txt.manage}
                </Link>
              </div>
            </>
          )}
        </div>
      </section>
    </main>
  );
};

export default BlogPage;
